import React from 'react';
import { AlertTriangle, BookOpen, CircleAlert, Hash, Heart, MapPin, Phone, Pill, ShieldAlert, ShieldCheck, Stethoscope, User, Utensils } from 'lucide-react';
import { Card, Field, FieldGrid } from './Card';

// The participant forms save 1 = Yes, 2 = No. Anything else (null, '', the API's schema default)
// means the question was never answered, so it falls through to Field's "Not provided".
const yesNo = value => ({ 1: 'Yes', 2: 'No' })[Number(value)] ?? null;

/**
 * Medical & Physician, plus the Insurance & Physician sub-section.
 *
 * Yes/no answers show "Not provided" until the user actually answers, rather than defaulting to
 * "No". `cleanList` is CamperDetail's removeStr, which strips the stored JSON-ish brackets and
 * quotes from the list fields (allergy, medical, dietary guidelines).
 */
const MedicalCard = ({ camper, cleanList }) => {
    const list = value => (value ? cleanList(value) : null);
    const allergy = list(camper?.allergy);

    return (
        <Card id="medical" title="Medical & Physician" icon={Stethoscope}>
            <FieldGrid className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 lg:tw-grid-cols-3">
                <Field icon={AlertTriangle} label="Allergies" tone={allergy ? 'warning' : 'default'}>{allergy}</Field>
                <Field icon={ShieldAlert} label="Allergy is life-threatening" tone={camper?.allergyDeadly === 1 ? 'danger' : 'success'}>
                    {yesNo(camper?.allergyDeadly)}
                </Field>
                <Field icon={Heart} label="Medical conditions">{list(camper?.medical)}</Field>
                <Field icon={Pill} label="Medications, dosage & special assistance">{camper?.medicationsTaken?.trim()}</Field>
                <Field icon={Pill} label="Brings own medication">{yesNo(camper?.participantBringingOwnMedication)}</Field>
                <Field icon={Pill} label="Receives counter medication" tone={camper?.participantReceiveCounterMedication === 1 ? 'warning' : 'default'}>
                    {yesNo(camper?.participantReceiveCounterMedication)}
                </Field>
                <Field icon={CircleAlert} label="Emergency medication permission" tone={camper?.permissionEmergencyMedication === 1 ? 'success' : 'danger'}>
                    {yesNo(camper?.permissionEmergencyMedication)}
                </Field>
                <Field icon={Utensils} label="Dietary guidelines">{list(camper?.dietaryGuidelines)}</Field>
                {/* No live form has asked this since the /addcampers cart form was orphaned (Mar 2025); stored
                    values are the API schema default (0 before 2026-06-30, 1 after), not answers. */}
                <Field icon={BookOpen} label="Religious diet">{null}</Field>
            </FieldGrid>

            <h3 className="tw-m-0 tw-mt-4 tw-border-t tw-border-neutral-200 tw-pb-3 tw-pt-4 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider tw-text-neutral-700">
                Insurance & Physician
            </h3>
            <FieldGrid className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 lg:tw-grid-cols-3">
                <Field icon={ShieldCheck} label="Medical insurance company">{camper?.medicalInsuranceCompany}</Field>
                <Field icon={Hash} label="Policy number">{camper?.policyNumber}</Field>
                <Field icon={Hash} label="Group #">{camper?.group}</Field>
                <Field icon={User} label="Primary physician">{camper?.primaryPhysician?.trim()}</Field>
                <Field icon={Phone} label="Physician phone">{camper?.physicianPhone?.trim()}</Field>
                <Field icon={MapPin} label="Physician address">{camper?.physicianAddress?.trim()}</Field>
            </FieldGrid>
        </Card>
    );
};

export default MedicalCard;
